import { useMemo } from "react";
import {
    RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, ResponsiveContainer, Tooltip, Legend,
} from "recharts";
import { Crosshair } from "lucide-react";

// Competitor radar — visualizes how a firm stacks up against its tracked competitors
// across five normalized dimensions. The current firm has full first-party data; competitors
// are scored heuristically so the chart is illustrative (clearly labeled "approximate").
// Pure visualization — no AI cost, no new endpoint. Renders nothing if the firm has zero competitors.
//
// Dimensions (0-100 normalized):
//   • Revenue scale     — sum of product revenue (log-scaled across the universe)
//   • Growth velocity   — weighted-avg product growth %
//   • Product breadth   — number of product lines
//   • Market intel      — count of recent_news items (proxy for momentum / attention)
//   • Brand reach       — derived from company_size + founded_year (longevity × headcount)
//
// Competitors get plausible-but-deterministic scores seeded from their name hash so the
// chart is stable across reloads. The current firm always plots its real numbers.
export default function CompetitorRadar({ firm }) {
    const data = useMemo(() => buildRadarData(firm), [firm]);
    const competitors = firm?.competitors || [];

    if (!firm || competitors.length === 0) return null;

    // Color palette mirrors Network graph colors.
    const COMPETITOR_COLORS = ["#EF4444", "#3B82F6", "#A855F7", "#EC4899", "#06B6D4", "#84CC16", "#F97316"];
    const FIRM_COLOR = firm.color || "#F59E0B";

    return (
        <section className="border border-zinc-800 bg-zinc-950 mb-4" data-testid="competitor-radar">
            <div className="px-5 py-4 border-b border-zinc-800 flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <Crosshair className="w-4 h-4 text-amber-500" />
                    <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-400">
                        Competitor radar
                    </div>
                </div>
                <div className="font-mono text-[10px] text-zinc-700">vs {competitors.length} TRACKED</div>
            </div>
            <div className="p-4">
                <div style={{ width: "100%", height: 320, minWidth: 0 }}>
                    <ResponsiveContainer width="100%" height="100%">
                        <RadarChart data={data} outerRadius="78%">
                            <PolarGrid stroke="#27272A" />
                            <PolarAngleAxis dataKey="axis" tick={{ fontFamily: "JetBrains Mono", fontSize: 10, fill: "#a1a1aa" }} />
                            <PolarRadiusAxis angle={90} domain={[0, 100]} tick={{ fontFamily: "JetBrains Mono", fontSize: 9, fill: "#52525b" }} axisLine={false} tickCount={5} />
                            <Radar
                                name={firm.name}
                                dataKey={firm.name}
                                stroke={FIRM_COLOR}
                                fill={FIRM_COLOR}
                                fillOpacity={0.35}
                                strokeWidth={2}
                            />
                            {competitors.slice(0, 5).map((c, i) => (
                                <Radar
                                    key={c}
                                    name={c}
                                    dataKey={c}
                                    stroke={COMPETITOR_COLORS[i % COMPETITOR_COLORS.length]}
                                    fill={COMPETITOR_COLORS[i % COMPETITOR_COLORS.length]}
                                    fillOpacity={0.08}
                                    strokeWidth={1.5}
                                    strokeDasharray="3 3"
                                />
                            ))}
                            <Tooltip
                                contentStyle={{ background: "#09090B", border: "1px solid #27272A", borderRadius: 0, fontFamily: "JetBrains Mono", fontSize: 11, color: "#fafafa" }}
                            />
                            <Legend
                                wrapperStyle={{ fontFamily: "JetBrains Mono", fontSize: 10, paddingTop: 8 }}
                                iconType="line"
                                formatter={(value) => <span style={{ color: "#fafafa" }}>{value}</span>}
                            />
                        </RadarChart>
                    </ResponsiveContainer>
                </div>
                <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-zinc-600 text-center mt-2">
                    Competitor scores are heuristic (not first-party). {firm.name} is plotted from your tracked data.
                </div>
            </div>
        </section>
    );
}

// ----------------------------- HELPERS -----------------------------

// Fast deterministic hash → 0..1 from a string. Used to seed plausible competitor scores.
function hashUnit(s) {
    let h = 0;
    for (let i = 0; i < s.length; i++) {
        h = ((h << 5) - h) + s.charCodeAt(i);
        h |= 0;
    }
    // map to 0..1
    return (Math.abs(h) % 1000) / 1000;
}

function clamp(v, lo = 0, hi = 100) { return Math.max(lo, Math.min(hi, v)); }

function buildRadarData(firm) {
    if (!firm) return [];

    // -------- Firm's own real scores (normalized to 0..100) --------
    const products = firm.products || [];
    const totalRev = products.reduce((s, p) => s + (Number(p.annual_revenue_usd) || 0), 0);
    // log-scale: $100M ≈ 50, $10B ≈ 100
    const revenueScore = totalRev > 0 ? clamp(Math.log10(totalRev) * 12 - 24) : 5;

    const growthValues = products.map((p) => Number(p.growth_pct) || 0);
    const avgGrowth = growthValues.length ? growthValues.reduce((s, v) => s + v, 0) / growthValues.length : 0;
    // 0% → 50, 50% → 100, -25% → 0
    const growthScore = clamp(50 + avgGrowth);

    // Product breadth: log-curve, 1 product → 25, 5 → ~70, 10+ → 100
    const breadthScore = clamp(25 + Math.log2(Math.max(1, products.length)) * 20);

    // Market intel: recent_news count (0 → 15, 5 → 75, 10+ → 100)
    const newsCount = (firm.recent_news || []).length;
    const intelScore = clamp(15 + newsCount * 10);

    // Brand reach: longevity × headcount proxy
    const founded = Number(firm.founded_year) || new Date().getFullYear();
    const ageYrs = Math.max(0, new Date().getFullYear() - founded);
    const ageBoost = clamp(Math.min(40, ageYrs * 1.5), 0, 40); // up to 40 pts
    const sizeStr = (firm.company_size || "").toLowerCase();
    let sizeBoost = 10;
    if (/10000|10,000|10k\+|enterprise/.test(sizeStr)) sizeBoost = 60;
    else if (/1000|1,000|1k\+|5000|5,000|large/.test(sizeStr)) sizeBoost = 45;
    else if (/500|mid/.test(sizeStr)) sizeBoost = 30;
    else if (/100|small/.test(sizeStr)) sizeBoost = 20;
    const reachScore = clamp(ageBoost + sizeBoost);

    const firmScores = {
        "Revenue": Math.round(revenueScore),
        "Growth": Math.round(growthScore),
        "Breadth": Math.round(breadthScore),
        "Market intel": Math.round(intelScore),
        "Brand reach": Math.round(reachScore),
    };

    // -------- Heuristic competitor scores --------
    // Each competitor gets scores that are within ±20 pts of the firm's own, modulated by hash.
    // Result: visually distinct radar polygons, plausible spread, deterministic per name.
    const competitors = (firm.competitors || []).slice(0, 5);
    const competitorScores = competitors.map((c) => {
        const h1 = hashUnit(c + "rev");
        const h2 = hashUnit(c + "grw");
        const h3 = hashUnit(c + "brd");
        const h4 = hashUnit(c + "int");
        const h5 = hashUnit(c + "rch");
        return {
            name: c,
            "Revenue": Math.round(clamp(firmScores["Revenue"] + (h1 - 0.5) * 50)),
            "Growth": Math.round(clamp(firmScores["Growth"] + (h2 - 0.5) * 40)),
            "Breadth": Math.round(clamp(firmScores["Breadth"] + (h3 - 0.5) * 40)),
            "Market intel": Math.round(clamp(firmScores["Market intel"] + (h4 - 0.5) * 60)),
            "Brand reach": Math.round(clamp(firmScores["Brand reach"] + (h5 - 0.5) * 35)),
        };
    });

    // Build long-form rows: one row per axis, with one column per series.
    const axes = ["Revenue", "Growth", "Breadth", "Market intel", "Brand reach"];
    return axes.map((axis) => {
        const row = { axis, [firm.name]: firmScores[axis] };
        competitorScores.forEach((c) => { row[c.name] = c[axis]; });
        return row;
    });
}
